import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ToastContainer } from "./components/Toast";
import { ValidationModal } from "./components/ValidationModal";
import { AuthGuard } from "./auth/AuthGuard";
import { GroupGuard } from "./auth/GroupGuard";
import { CallbackPage } from "./auth/CallbackPage";
import { FormPage } from "./pages/FormPage";
import { EnvPickerPage } from "./pages/EnvPickerPage";
import { getConfig } from "./config";
import { getTabSfEnv, pickSfEnvFromUrl, setTabSfEnv } from "./sfEnv";

function EnvGate({ children }: { children: React.ReactNode }) {
  const config = getConfig();

  // Honor an explicit ?sfEnv= from the invite link when it matches an allowed environment. Set before the
  // picker check (and before the AuthGuard/API layer reads it) so the link can target a specific SF org on a
  // shared test deployment — skipping the picker. Ignored on a single-env deployment whose allowed list
  // doesn't contain the value (e.g. production).
  const linkEnv = pickSfEnvFromUrl(window.location.search, config.AVAILABLE_SF_ENVIRONMENTS);
  if (linkEnv && getTabSfEnv() !== linkEnv) {
    setTabSfEnv(linkEnv);
  }

  if (config.SHOW_ENV_PICKER && !getTabSfEnv()) {
    const next = encodeURIComponent(window.location.pathname + window.location.search);
    return <Navigate to={`/env?next=${next}`} replace />;
  }
  return <>{children}</>;
}

/**
 * Root app component. React Router handles top-level routing:
 *   /auth/callback → OIDC callback handler
 *   /env           → SF environment picker (test deployments only)
 *   /intake        → AuthGuard → GroupGuard → FormPage (8-step wizard)
 *   *              → redirect to /intake
 *
 * Step navigation within FormPage is state-driven (not URL-driven).
 */
export function App() {
  const config = getConfig();

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ToastContainer />
        <ValidationModal />
        <Routes>
          <Route path="/auth/callback" element={<CallbackPage />} />
          <Route path="/env" element={config.SHOW_ENV_PICKER ? <EnvPickerPage /> : <Navigate to="/" replace />} />
          <Route
            path="/intake"
            element={
              <EnvGate>
                <AuthGuard>
                  <GroupGuard>
                    <FormPage />
                  </GroupGuard>
                </AuthGuard>
              </EnvGate>
            }
          />
          <Route path="*" element={<Navigate to="/intake" replace />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
